import { User } from "@phosphor-icons/react"
import { startCase } from "lodash"
import { useMemo } from "react"
import { type ReactSelectOption } from "~/components/reusable/Select"
import {
  useCollectionSkuExtraDataKeys,
  useExtraDataFormattedKeys,
  useExtraDataTypeMap,
  useIncomingOrderExtraDataKeys,
} from "~/hooks/useSku"
import {
  CARRY_OVER_LABEL,
  computeHiddenFilterFields,
  disabledFilterFields,
  filterFields as filterFieldsRaw,
  type FilterFieldType,
  filterVariants,
  NEW_VARIANT_LABEL,
  SKU_SIZE_DIMENSION_1_FILTER_FIELD,
  SKU_SIZE_DIMENSION_2_FILTER_FIELD,
  SKU_SIZE_FILTER_FIELD,
  sortFilterFields,
} from "~/utils/filter"
import { compareSizeDimensionValues } from "~/utils/sizeDimensions"
import { sortSizesComparator } from "~/utils/sizeSplit"
import { filterNotUndefinedOrNull, stringFromCamelCase, uniq } from "~/utils/tsUtils"
import { ExtraDataTable, getExtraDataType } from "~/utils/dynamicTableUtils"
import {
  collectRelevantValues,
  type GroupedFieldOptions,
  splitGroupedOptions,
} from "~/utils/groupedFilterOptions"
import { useCollectionFilter, useGetCollections } from "~/hooks/useCollections"
import { useFilterFieldsOverride } from "~/hooks/useFilterFieldsOverride"
import { useFilters } from "~/hooks/useFilters"
import { useForecastModels } from "~/hooks/useForecasts"
import { useProductDetails } from "~/hooks/useProductDetails"
import { useVariants } from "~/hooks/useVariants"
import { useGetTags } from "~/hooks/useTags"
import { type IVariantSummary } from "~/hooks/useVariantTableData"

export type { GroupedFieldOptions } from "~/utils/groupedFilterOptions"

// Stable reference for the "no grouping" result so consumer useMemos don't churn.
const EMPTY_GROUPED: GroupedFieldOptions = { relevant: [], other: [] }
// Stable empty default so the allFilterFields memo doesn't churn for callers without extras.
const EMPTY_FILTER_FIELDS: FilterFieldType[] = []
const EMPTY_HIDDEN_FIELDS = new Set<string>()

export function useFilterFields(
  filterSearch: string = "",
  extraFilterFields: FilterFieldType[] = EMPTY_FILTER_FIELDS,
) {
  const { data: extraDataFields } = useExtraDataFormattedKeys()
  const { data: collectionExtraDataFields } = useCollectionSkuExtraDataKeys()
  const { data: incomingOrderExtraDataFields } = useIncomingOrderExtraDataKeys()
  const { data: colTypeMap } = useExtraDataTypeMap()
  const { data: tags, isLoading: tagsLoading } = useGetTags()
  const { data: forecastModels, isLoading: forecastModelsLoading } = useForecastModels()
  const collections = useCollectionFilter()
  const { isLoading: collectionsLoading } = useGetCollections()
  const productDetails = useProductDetails()
  const { data: brands, isLoading: brandsLoading } = productDetails.brands
  const { data: categories, isLoading: categoriesLoading } = productDetails.categories
  const { data: colors, isLoading: colorsLoading } = productDetails.colors
  const { data: vendors, isLoading: vendorsLoading } = productDetails.vendors
  const { data: genders, isLoading: gendersLoading } = productDetails.genders
  const { data: sizes } = productDetails.sizes
  const variants = useVariants()
  const { filters } = useFilters()
  const override = useFilterFieldsOverride()

  // SKU sizes need the domain-aware sort (S/M/L before numeric bands), not the query's
  // alphanumeric order, so re-sort the distinct list here before it feeds the value dropdown.
  const sortedSizeOptions = useMemo(
    () => [...(sizes ?? [])].sort((a, b) => sortSizesComparator(a.value ?? "", b.value ?? "")),
    [sizes],
  )

  // The ingested dimensions, never the raw size.
  const sizeDimensionOptions = useMemo(() => {
    const options = (key: "sizeDimension1" | "sizeDimension2") =>
      uniq(variants.flatMap((variant) => variant[key] ?? []))
        .sort(compareSizeDimensionValues)
        .map((value) => ({ value }))
    return { dimension1: options("sizeDimension1"), dimension2: options("sizeDimension2") }
  }, [variants])

  const forecastModelsOptions = useMemo(
    () =>
      forecastModels?.map(
        (model) => ({ value: model, label: model }) as ReactSelectOption<string>,
      ) ?? [],
    [forecastModels],
  )

  const enumLookupTable = useMemo(
    () => ({
      brand: brands,
      category: categories,
      color: colors,
      vendors: vendors,
      preferredVendorId: vendors,
      gender: genders,
      [SKU_SIZE_FILTER_FIELD]: sortedSizeOptions,
      [SKU_SIZE_DIMENSION_1_FILTER_FIELD]: sizeDimensionOptions.dimension1,
      [SKU_SIZE_DIMENSION_2_FILTER_FIELD]: sizeDimensionOptions.dimension2,
      collections: collections,
      productType: [{ value: "NOOS" }, { value: "Fashion" }],
      abc: [{ value: "A" }, { value: "B" }, { value: "C" }],
      lifecycleStatus: [
        { value: "Upcoming" },
        { value: "Active" },
        { value: "Phasing out" },
        { value: "Phased out" },
      ],
      demandVariantType: [{ value: NEW_VARIANT_LABEL }, { value: CARRY_OVER_LABEL }],
    }),
    [
      brands,
      categories,
      colors,
      vendors,
      genders,
      sortedSizeOptions,
      sizeDimensionOptions,
      collections,
    ],
  )

  const dynamicFilters: FilterFieldType[] = useMemo(() => {
    const extraData =
      extraDataFields
        ?.filter((x) => !filterFieldsRaw.find((y) => y.value == x))
        .map((x) => ({
          value: "DynamicFilterField",
          dynamicValue: x,
          name: stringFromCamelCase(x.replace("(extradata)", "")),
          type: getExtraDataType(x.replace("(extradata)", ""), ExtraDataTable.SKU, colTypeMap),
        })) ?? []

    const collectionExtraData =
      collectionExtraDataFields
        ?.filter((x) => !filterFieldsRaw.find((y) => y.value == x))
        .map((x) => ({
          value: "DynamicFilterField",
          dynamicValue: x,
          name: stringFromCamelCase(x.replace("(collection-extradata)", "")),
          type: getExtraDataType(
            x.replace("(collection-extradata)", ""),
            ExtraDataTable.COLLECTION_ON_SKU,
            colTypeMap,
          ),
        })) ?? []

    const incomingOrderExtraData =
      incomingOrderExtraDataFields
        ?.filter((x) => !filterFieldsRaw.find((y) => y.value == x))
        .map((x) => ({
          value: "DynamicFilterField",
          dynamicValue: x,
          name: stringFromCamelCase(x.replace("(incoming-order-extradata)", "")),
          type: getExtraDataType(
            x.replace("(incoming-order-extradata)", ""),
            ExtraDataTable.INCOMING_ORDER_ITEM,
            colTypeMap,
          ),
        })) ?? []

    return [...extraData, ...collectionExtraData, ...incomingOrderExtraData]
  }, [extraDataFields, collectionExtraDataFields, incomingOrderExtraDataFields, colTypeMap])

  const allFilterFields = useMemo(() => {
    if (override) return override.fields
    // extraFilterFields are context-specific (e.g. the Fashion Supply Plan's SKU Size), appended
    // rather than replacing so dynamic/extradata fields are preserved.
    return [...filterFieldsRaw, ...dynamicFilters, ...extraFilterFields].sort(sortFilterFields)
  }, [override, dynamicFilters, extraFilterFields])

  const quickFilterFields = useMemo(() => {
    return allFilterFields
      .filter((x: FilterFieldType) => x.type === "boolean")
      .filter((x: FilterFieldType) => x.name.toLowerCase().includes(filterSearch.toLowerCase()))
  }, [allFilterFields, filterSearch])

  // Hide filters whose backing value list has loaded and is empty for the
  // current tenant. Filters stay visible while data is still pending so they
  // don't briefly disappear during initial load.
  const hiddenFilterFields = useMemo(() => {
    if (override) return EMPTY_HIDDEN_FIELDS
    const hasAnyValue = (data: { value?: unknown }[] | undefined) =>
      !!data?.some((x) => x?.value != null && String(x.value).length > 0)
    return computeHiddenFilterFields({
      brand: { hasValues: hasAnyValue(brands), isLoading: brandsLoading },
      category: { hasValues: hasAnyValue(categories), isLoading: categoriesLoading },
      color: { hasValues: hasAnyValue(colors), isLoading: colorsLoading },
      gender: { hasValues: hasAnyValue(genders), isLoading: gendersLoading },
      vendors: { hasValues: hasAnyValue(vendors), isLoading: vendorsLoading },
      preferredVendorId: { hasValues: hasAnyValue(vendors), isLoading: vendorsLoading },
      collections: { hasValues: collections.length > 0, isLoading: collectionsLoading },
      tags: { hasValues: (tags?.length ?? 0) > 0, isLoading: tagsLoading },
      model: { hasValues: (forecastModels?.length ?? 0) > 0, isLoading: forecastModelsLoading },
    })
  }, [
    override,
    brands,
    brandsLoading,
    categories,
    categoriesLoading,
    colors,
    colorsLoading,
    genders,
    gendersLoading,
    vendors,
    vendorsLoading,
    collections,
    collectionsLoading,
    tags,
    tagsLoading,
    forecastModels,
    forecastModelsLoading,
  ])

  const filterFields = useMemo(() => {
    return allFilterFields
      .filter((x: FilterFieldType) => x.name.toLowerCase().includes(filterSearch.toLowerCase()))
      .filter((x: FilterFieldType) => !disabledFilterFields.includes(x.value))
      .filter((x: FilterFieldType) => !hiddenFilterFields.has(x.value))
  }, [allFilterFields, filterSearch, hiddenFilterFields])

  const nonBooleanQuickFilters = useMemo(() => {
    if (override) return []
    return [
      {
        name: "My SKUs",
        value: "userId",
        type: "user",
        icon: <User />,
      },
      {
        name: "NOOS",
        value: "productType",
        type: "string",
      },
    ].filter((x) => x.name.toLowerCase().includes(filterSearch.toLowerCase()))
  }, [override, filterSearch])

  const filterFieldOptions = useMemo(
    () =>
      allFilterFields
        .filter((x) => !hiddenFilterFields.has(x.value))
        .map((field: FilterFieldType) => ({
          label: startCase(field.name),
          value: field.value === "DynamicFilterField" ? field.dynamicValue : field.value,
        })),
    [allFilterFields, hiddenFilterFields],
  )

  // Helper function to compute options for a field
  const computeOptionsForField = useMemo(
    () =>
      (fieldValue: string, fieldType?: string): string[] => {
        if (override) {
          if (fieldType === "boolean") return ["true", "false"]
          return override.optionsByField?.[fieldValue] ?? []
        }
        const declared = allFilterFields.find((f) => f.value === fieldValue)?.options
        if (declared) return [...declared]
        let computedOptions: string[] = []
        if (enumLookupTable[fieldValue as keyof typeof enumLookupTable]) {
          computedOptions = filterNotUndefinedOrNull(
            enumLookupTable[fieldValue as keyof typeof enumLookupTable] ?? [],
          )
            .filter((item: any) => item.value)
            .map((item: any) => item.value?.toString() ?? "")
        } else if (fieldValue === "tags" && tags) {
          computedOptions = tags.map((tag) => tag.name)
        } else if ((extraDataFields ?? []).includes(fieldValue?.replace("(extradata)", "") ?? "")) {
          const extraDataKey = fieldValue?.replace("(extradata)", "") ?? ""
          computedOptions = filterNotUndefinedOrNull(
            variants.map((variant) => (variant.extraData as any)[extraDataKey]),
          ).map((item) => item.toString())
        } else if (fieldValue.includes("(incoming-order-extradata)")) {
          const key = fieldValue.replace("(incoming-order-extradata)", "")
          computedOptions = filterNotUndefinedOrNull(
            variants.flatMap(
              (v) => v.incomingOrders?.map((o) => (o.extraData as any)?.[key]) ?? [],
            ),
          ).map((item) => String(item))
        } else if (fieldType === "string") {
          computedOptions = filterNotUndefinedOrNull(
            variants.map((variant) => variant[fieldValue as keyof IVariantSummary]),
          ).map((item) => String(item))
        } else if (fieldType === "boolean") {
          computedOptions = ["true", "false"]
        }
        return uniq(computedOptions)
      },
    [override, variants, tags, extraDataFields, enumLookupTable, allFilterFields],
  )

  const computeGroupedOptionsForField = useMemo(
    () =>
      (fieldValue: string, fieldType: string | undefined): GroupedFieldOptions => {
        // Grouping only applies to enum-ish and extradata fields. Skip the variant
        // scan for everything else (string text input, date, boolean, user, …).
        const usesGrouping =
          fieldType === "enum" ||
          fieldValue.includes("(extradata)") ||
          fieldValue.includes("(collection-extradata)")
        if (!usesGrouping || override) return EMPTY_GROUPED
        // A declared list is the page's own vocabulary; the variant store it would be grouped against
        // does not carry the field.
        if (allFilterFields.some((f) => f.value === fieldValue && f.options)) return EMPTY_GROUPED

        const allOptions = computeOptionsForField(fieldValue, fieldType)
        if (allOptions.length === 0) return EMPTY_GROUPED

        if (filters.length === 0) return EMPTY_GROUPED

        const contextVariants = filterVariants(filters, variants)
        const relevantSet = collectRelevantValues(fieldValue, contextVariants)
        return splitGroupedOptions(allOptions, relevantSet)
      },
    [override, computeOptionsForField, filters, variants, allFilterFields],
  )

  return {
    allFilterFields,
    filterFields,
    quickFilterFields,
    nonBooleanQuickFilters,
    filterFieldOptions,
    enumLookupTable,
    forecastModelsOptions,
    computeOptionsForField,
    computeGroupedOptionsForField,
    tags,
    collections,
  }
}
